export type UserRole = 'customer' | 'tower' | 'garage' | 'parts_vendor' | 'insurer' | 'admin';

export type JobStatus =
  | 'pending'
  | 'dispatched'
  | 'en_route'
  | 'arrived'
  | 'towing'
  | 'completed'
  | 'delivered_to_garage'
  | 'payment_pending'
  | 'payment_completed'
  | 'reimbursement_pending'
  | 'reimbursement_completed'
  | 'insurance_claim_pending'
  | 'insurance_claim_completed'
  | 'cancelled';

export type ServiceType =
  | 'towing'
  | 'battery'
  | 'flat_tyre'
  | 'fuel'
  | 'parts'
  | 'insurance_claim';

export type PaymentMethod = 'mpesa' | 'card' | 'cash' | 'insurer';

export type ClaimStatus = 'pending' | 'approved' | 'flagged' | 'disputed' | 'paid' | 'rejected';

export type PolicyStatus = 'active' | 'expired' | 'suspended' | 'cancelled';
export type CoverageType = 'comprehensive' | 'third_party' | 'roadside_assist';
export type ClaimDocumentType = 'photo' | 'estimate' | 'invoice' | 'police_report' | 'other';
export type ClaimAuditAction = 'created' | 'approved' | 'disputed' | 'flagged' | 'unflagged' | 'paid' | 'document_added' | 'note_added' | 'status_changed' | 'settlement_requested';

export type PartsCondition = 'genuine_oem' | 'quality_used' | 'aftermarket';

export type NotificationType =
  | 'info'
  | 'job'
  | 'claim'
  | 'parts'
  | 'payment'
  | 'alert';

export interface Profile {
  id: string;
  role: UserRole; // Primary role for backward compatibility
  roles?: UserRole[]; // Array of roles for multi-role support
  full_name: string;
  phone: string;
  avatar_url: string;
  ntsa_certified: boolean;
  truck_type: string;
  truck_plate: string;
  is_online: boolean;
  kebs_certified: boolean;
  business_name: string;
  insurer_name: string;
  insurer_since: number | null;
  is_suspended: boolean;
  default_lat: number;
  default_lng: number;
  service_area_km: number;
  created_at: string;
  updated_at: string;
  // Tower pricing fields
  base_towing_rate: number;
  per_km_rate: number;
  battery_service_rate: number;
  flat_tyre_rate: number;
  fuel_delivery_rate: number;
}

export interface Vehicle {
  id: string;
  owner_id: string;
  plate: string;
  make: string;
  model: string;
  year: number | null;
  color: string;
  insurance_policy_number: string;
  insurer_name: string;
  policy_active: boolean;
  created_at: string;
}

export interface TowJob {
  id: string;
  reference: string;
  customer_id: string;
  operator_id: string | null;
  vehicle_id: string | null;
  status: JobStatus;
  service_type: ServiceType;
  pickup_address: string;
  pickup_lat: number;
  pickup_lng: number;
  destination_address: string;
  destination_lat: number | null;
  destination_lng: number | null;
  issue_description: string;
  diagnosis_notes: string;
  diagnosis_photos: string[];
  payment_method: PaymentMethod;
  total_amount: number;
  insurer_payout: number;
  customer_excess: number;
  operator_distance_km: number | null;
  insurer_id_selected: string | null;
  service_price: number;
  created_at: string;
  updated_at: string;
  en_route_at: string | null;
  completed_at: string | null;
  // Platform payment tracking fields
  platform_paid_to_operator: boolean;
  platform_paid_at: string | null;
  platform_payment_amount: number;
  platform_payment_reference: string;
  insurance_claim_generated: boolean;
  insurance_claim_id: string | null;
  // Garage destination field
  destination_garage_id: string | null;
  // Tower selection fields
  selected_tower_id: string | null;
  tower_accepted_at: string | null;
  tower_declined_at: string | null;
}

export interface TowerGarageTransaction {
  id: string;
  job_id: string;
  tower_id: string;
  garage_id: string;
  amount: number;
  payment_method: 'cash' | 'mpesa' | 'card' | 'bank_transfer';
  payment_reference: string;
  document_number: string;
  notes: string;
  paid_at: string;
  created_at: string;
  updated_at: string;
  auto_generated_ref: boolean;
  auto_generated_doc: boolean;
}

export interface GarageReimbursement {
  id: string;
  garage_id: string;
  total_amount: number;
  transaction_count: number;
  transaction_ids: string[];
  payment_method: 'bank_transfer' | 'mpesa' | 'check';
  payment_reference: string;
  reimbursement_period_start: string;
  reimbursement_period_end: string;
  processed_at: string | null;
  processed_by: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface PartsRequest {
  id: string;
  job_id: string;
  description: string;
  created_at: string;
}

export interface PartsQuote {
  id: string;
  request_id: string;
  vendor_id: string;
  price: number;
  condition: PartsCondition;
  kebs_certified: boolean;
  notes: string;
  selected: boolean;
  created_at: string;
}

export interface Claim {
  id: string;
  reference: string;
  job_id: string | null;
  customer_id: string;
  insurer_id: string | null;
  policy_id: string | null;
  policy_holder_name: string;
  policy_number: string;
  insurer_name: string;
  event_description: string;
  status: ClaimStatus;
  total_amount: number;
  insurer_payout: number;
  customer_excess: number;
  fraud_flag: boolean;
  fraud_notes: string;
  approved_at: string | null;
  paid_at: string | null;
  coverage_type: string;
  coverage_limit: number;
  settlement_reference: string;
  settlement_date: string | null;
  insurer_notes: string;
  event_date: string;
  vehicle_plate: string;
  vehicle_make_model: string;
  operator_name: string;
  operator_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  job_id: string;
  sender_id: string;
  recipient_id: string;
  message: string;
  created_at: string;
  read_at: string | null;
}

export interface InsurancePolicy {
  id: string;
  insurer_id: string | null;
  customer_id: string;
  vehicle_id: string | null;
  policy_number: string;
  policy_holder_name: string;
  insurer_name: string;
  coverage_type: CoverageType;
  coverage_limit: number;
  excess_amount: number;
  status: PolicyStatus;
  valid_from: string;
  valid_until: string;
  created_at: string;
  updated_at: string;
}

export interface ClaimDocument {
  id: string;
  claim_id: string;
  uploaded_by: string;
  document_type: ClaimDocumentType;
  file_url: string;
  file_name: string;
  notes: string;
  created_at: string;
}

export interface ClaimAuditEntry {
  id: string;
  claim_id: string;
  actor_id: string;
  action: ClaimAuditAction;
  old_status: string | null;
  new_status: string | null;
  notes: string;
  created_at: string;
}

export interface AppNotification {
  id: string;
  user_id: string;
  title: string;
  body: string;
  type: NotificationType;
  read: boolean;
  job_id: string | null;
  claim_id: string | null;
  created_at: string;
}

export interface InsurerDirectoryEntry {
  id: string;
  name: string;
  logo_url: string;
  contact_phone: string;
  contact_email: string;
  is_active: boolean;
  created_at: string;
}

export interface ServicePricing {
  id: string;
  service_type: ServiceType;
  base_price: number;
  per_km_rate: number;
  is_active: boolean;
  created_at: string;
}

export interface OperatorLocation {
  operator_id: string;
  lat: number;
  lng: number;
  last_seen: string;
  updated_at: string;
}

export interface Part {
  id: string;
  vendor_id: string;
  name: string;
  description: string;
  category: string;
  brand: string;
  condition: string;
  price: number;
  stock: number;
  image_url: string;
  location: string;
  is_active: boolean;
  compatible_makes: string;
  compatible_models: string;
  created_at: string;
  updated_at: string;
}

export interface PartOrder {
  id: string;
  customer_id: string;
  vendor_id: string;
  part_id: string;
  part_name: string;
  part_image_url: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  delivery_location: string;
  customer_note: string;
  status: 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
  created_at: string;
}

export interface CartItem {
  id: string;
  customer_id: string;
  part_id: string;
  quantity: number;
  created_at: string;
}

export type PartOrderStatus = 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'cancelled';

export const PART_ORDER_STATUS_LABELS: Record<PartOrderStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  processing: 'Processing',
  ready: 'Ready',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export const PART_ORDER_STATUS_COLORS: Record<PartOrderStatus, string> = {
  pending: 'badge-warning',
  confirmed: 'badge-primary',
  processing: 'badge-purple',
  ready: 'badge-cyan',
  delivered: 'badge-success',
  cancelled: 'badge-gray',
};

export const PART_CATEGORIES = [
  'Engine', 'Brakes', 'Electrical', 'Body', 'Suspension',
  'Transmission', 'Tyres', 'Accessories', 'Filters', 'Other',
] as const;

export const PART_CONDITION_LABELS: Record<string, string> = {
  new: 'New',
  used: 'Used',
  refurbished: 'Refurbished',
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  pending: 'Pending',
  dispatched: 'Dispatched',
  en_route: 'En Route',
  arrived: 'Arrived',
  towing: 'Towing',
  completed: 'Completed',
  delivered_to_garage: 'Delivered to Garage',
  payment_pending: 'Payment Pending',
  payment_completed: 'Payment Completed',
  reimbursement_pending: 'Reimbursement Pending',
  reimbursement_completed: 'Reimbursement Completed',
  insurance_claim_pending: 'Insurance Claim Pending',
  insurance_claim_completed: 'Insurance Claim Completed',
  cancelled: 'Cancelled',
};

export const JOB_STATUS_COLORS: Record<JobStatus, string> = {
  pending: 'badge-warning',
  dispatched: 'badge-primary',
  en_route: 'badge-primary',
  arrived: 'badge-primary',
  towing: 'badge-accent',
  completed: 'badge-success',
  delivered_to_garage: 'badge-info',
  payment_pending: 'badge-warning',
  payment_completed: 'badge-success',
  reimbursement_pending: 'badge-warning',
  reimbursement_completed: 'badge-success',
  insurance_claim_pending: 'badge-primary',
  insurance_claim_completed: 'badge-success',
  cancelled: 'badge-gray',
};

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  towing: 'Towing',
  battery: 'Battery Jumpstart',
  flat_tyre: 'Flat Tyre',
  fuel: 'Fuel Delivery',
  parts: 'Spare Parts',
  insurance_claim: 'Insurance Claim',
};

export const CLAIM_STATUS_LABELS: Record<ClaimStatus, string> = {
  pending: 'Pending Review',
  approved: 'Approved',
  flagged: 'Flagged',
  disputed: 'Disputed',
  paid: 'Settled',
  rejected: 'Rejected',
};

export const CLAIM_STATUS_COLORS: Record<ClaimStatus, string> = {
  pending: 'badge-warning',
  approved: 'badge-success',
  flagged: 'badge-error',
  disputed: 'badge-error',
  paid: 'badge-primary',
  rejected: 'badge-gray',
};

export const POLICY_STATUS_LABELS: Record<PolicyStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

export const POLICY_STATUS_COLORS: Record<PolicyStatus, string> = {
  active: 'badge-success',
  expired: 'badge-gray',
  suspended: 'badge-warning',
  cancelled: 'badge-error',
};

export const COVERAGE_TYPE_LABELS: Record<CoverageType, string> = {
  comprehensive: 'Comprehensive',
  third_party: 'Third Party',
  roadside_assist: 'Roadside Assistance',
};

export const CLAIM_DOC_TYPE_LABELS: Record<ClaimDocumentType, string> = {
  photo: 'Incident Photo',
  estimate: 'Repair Estimate',
  invoice: 'Invoice',
  police_report: 'Police Report',
  other: 'Other Document',
};

export const PARTS_CONDITION_LABELS: Record<PartsCondition, string> = {
  genuine_oem: 'Genuine OEM',
  quality_used: 'Quality Used',
  aftermarket: 'Aftermarket',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  customer: 'Customer',
  tower: 'Tower',
  garage: 'Garage',
  parts_vendor: 'Parts Shop',
  insurer: 'Insurance Company',
  admin: 'Administrator',
};

export function formatKES(amount: number): string {
  return 'KES ' + amount.toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
